import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  getAuthToken,
  getCurrentShopId,
  getCurrentUserRole,
} from "./auth";
import "./SuppliersPage.css";

const API_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000";

const emptyForm = {
  name: "",
  contact_name: "",
  email: "",
  phone: "",
  address_line_1: "",
  address_line_2: "",
  city: "",
  county: "",
  postcode: "",
  country: "Ireland",
  account_number: "",
  vat_number: "",
  payment_terms_days: "0",
  notes: "",
  active: true,
};

function SuppliersPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingSupplierId, setEditingSupplierId] = useState(null);

  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const token = getAuthToken();
  const shopId = getCurrentShopId();
  const role = getCurrentUserRole();

  const buildAuthConfig = () => {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    if (role === "super_admin") {
      config.params = {
        shop_id: shopId,
      };
    }

    return config;
  };

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      setError("");

      const config = buildAuthConfig();

      config.params = {
        ...(config.params || {}),
        include_inactive: includeInactive,
      };

      const response = await axios.get(
        `${API_URL}/suppliers`,
        config
      );

      setSuppliers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error(
        "Load suppliers error:",
        err.response?.data || err.message
      );

      setError(
        err.response?.data?.error || "Failed to load suppliers"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive]);

  const filteredSuppliers = useMemo(() => {
    const searchText = search.trim().toLowerCase();

    if (!searchText) {
      return suppliers;
    }

    return suppliers.filter((supplier) => {
      return [
        supplier.name,
        supplier.contact_name,
        supplier.email,
        supplier.phone,
        supplier.account_number,
        supplier.vat_number,
        supplier.city,
      ].some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(searchText)
      );
    });
  }, [suppliers, search]);

  const updateField = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));

    setFieldErrors((previous) => ({
      ...previous,
      [field]: "",
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (!form.name.trim()) {
      errors.name = "Supplier name is required";
    } else if (form.name.trim().length > 150) {
      errors.name = "Supplier name cannot exceed 150 characters";
    }

    if (
      form.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())
    ) {
      errors.email = "Enter a valid email address";
    }

    const paymentTerms = Number(form.payment_terms_days);

    if (
      !Number.isInteger(paymentTerms) ||
      paymentTerms < 0 ||
      paymentTerms > 3650
    ) {
      errors.payment_terms_days =
        "Enter a valid number of payment-term days";
    }

    setFieldErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const openCreateModal = () => {
    setEditingSupplierId(null);
    setForm(emptyForm);
    setFieldErrors({});
    setError("");
    setSuccessMessage("");
    setModalOpen(true);
  };

  const openEditModal = (supplier) => {
    setEditingSupplierId(supplier.id);

    setForm({
      name: supplier.name || "",
      contact_name: supplier.contact_name || "",
      email: supplier.email || "",
      phone: supplier.phone || "",
      address_line_1: supplier.address_line_1 || "",
      address_line_2: supplier.address_line_2 || "",
      city: supplier.city || "",
      county: supplier.county || "",
      postcode: supplier.postcode || "",
      country: supplier.country || "Ireland",
      account_number: supplier.account_number || "",
      vat_number: supplier.vat_number || "",
      payment_terms_days: String(
        supplier.payment_terms_days ?? 0
      ),
      notes: supplier.notes || "",
      active: supplier.active !== false,
    });

    setFieldErrors({});
    setError("");
    setSuccessMessage("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;

    setModalOpen(false);
    setEditingSupplierId(null);
    setForm(emptyForm);
    setFieldErrors({});
  };

  const saveSupplier = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccessMessage("");

      const payload = {
        ...form,
        name: form.name.trim(),
        contact_name: form.contact_name.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        address_line_1: form.address_line_1.trim() || null,
        address_line_2: form.address_line_2.trim() || null,
        city: form.city.trim() || null,
        county: form.county.trim() || null,
        postcode: form.postcode.trim() || null,
        country: form.country.trim() || "Ireland",
        account_number: form.account_number.trim() || null,
        vat_number: form.vat_number.trim() || null,
        payment_terms_days: Number(form.payment_terms_days || 0),
        notes: form.notes.trim() || null,
        active: form.active,
      };

      if (role === "super_admin") {
        payload.shop_id = shopId;
      }

      const config = buildAuthConfig();

      if (editingSupplierId) {
        await axios.put(
          `${API_URL}/suppliers/${editingSupplierId}`,
          payload,
          config
        );

        setSuccessMessage("Supplier updated successfully");
      } else {
        await axios.post(
          `${API_URL}/suppliers`,
          payload,
          config
        );

        setSuccessMessage("Supplier created successfully");
      }

      closeModal();
      await loadSuppliers();
    } catch (err) {
      console.error(
        "Save supplier error:",
        err.response?.data || err.message
      );

      setError(
        err.response?.data?.error || "Failed to save supplier"
      );
    } finally {
      setSaving(false);
    }
  };

  const deactivateSupplier = async (supplier) => {
    const confirmed = window.confirm(
      `Deactivate "${supplier.name}"?\n\nThe supplier will remain in purchase history but will no longer appear in the active supplier list.`
    );

    if (!confirmed) return;

    try {
      setError("");
      setSuccessMessage("");

      await axios.delete(
        `${API_URL}/suppliers/${supplier.id}`,
        buildAuthConfig()
      );

      setSuccessMessage("Supplier deactivated successfully");
      await loadSuppliers();
    } catch (err) {
      console.error(
        "Deactivate supplier error:",
        err.response?.data || err.message
      );

      setError(
        err.response?.data?.error ||
          "Failed to deactivate supplier"
      );
    }
  };

  const reactivateSupplier = async (supplier) => {
    try {
      setError("");
      setSuccessMessage("");

      const payload = {
        name: supplier.name,
        contact_name: supplier.contact_name,
        email: supplier.email,
        phone: supplier.phone,
        address_line_1: supplier.address_line_1,
        address_line_2: supplier.address_line_2,
        city: supplier.city,
        county: supplier.county,
        postcode: supplier.postcode,
        country: supplier.country || "Ireland",
        account_number: supplier.account_number,
        vat_number: supplier.vat_number,
        payment_terms_days: Number(
          supplier.payment_terms_days || 0
        ),
        notes: supplier.notes,
        active: true,
      };

      if (role === "super_admin") {
        payload.shop_id = shopId;
      }

      await axios.put(
        `${API_URL}/suppliers/${supplier.id}`,
        payload,
        buildAuthConfig()
      );

      setSuccessMessage("Supplier reactivated successfully");
      await loadSuppliers();
    } catch (err) {
      console.error(
        "Reactivate supplier error:",
        err.response?.data || err.message
      );

      setError(
        err.response?.data?.error ||
          "Failed to reactivate supplier"
      );
    }
  };

  return (
    <div className="suppliers-page">
      <div className="suppliers-page-header">
        <div>
          <p className="suppliers-eyebrow">Stock management</p>
          <h1>Suppliers</h1>
          <p className="suppliers-subtitle">
            Manage supplier contact details, account information and
            payment terms.
          </p>
        </div>

        <button
          type="button"
          className="supplier-primary-btn"
          onClick={openCreateModal}
        >
          + New Supplier
        </button>
      </div>

      {error && (
        <div className="supplier-alert supplier-alert-error">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="supplier-alert supplier-alert-success">
          {successMessage}
        </div>
      )}

      <div className="supplier-toolbar">
        <div className="supplier-search-wrap">
          <span className="supplier-search-icon">⌕</span>

          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search suppliers..."
          />
        </div>

        <label className="supplier-toggle">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(event) =>
              setIncludeInactive(event.target.checked)
            }
          />

          <span>Show inactive suppliers</span>
        </label>
      </div>

      <div className="supplier-table-card">
        {loading ? (
          <div className="supplier-state">
            Loading suppliers...
          </div>
        ) : filteredSuppliers.length === 0 ? (
          <div className="supplier-empty-state">
            <div className="supplier-empty-icon">S</div>

            <h2>
              {search
                ? "No matching suppliers"
                : "No suppliers added yet"}
            </h2>

            <p>
              {search
                ? "Try another search term."
                : "Add your first supplier to start managing purchase information."}
            </p>

            {!search && (
              <button
                type="button"
                className="supplier-primary-btn"
                onClick={openCreateModal}
              >
                + Add First Supplier
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="supplier-result-count">
              {filteredSuppliers.length} supplier
              {filteredSuppliers.length === 1 ? "" : "s"}
            </div>

            <div className="supplier-table-scroll">
              <table className="supplier-table">
                <thead>
                  <tr>
                    <th>Supplier</th>
                    <th>Contact</th>
                    <th>Account</th>
                    <th>Payment Terms</th>
                    <th>Status</th>
                    <th className="supplier-actions-heading">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filteredSuppliers.map((supplier) => (
                    <tr
                      key={supplier.id}
                      className={
                        supplier.active
                          ? ""
                          : "supplier-inactive-row"
                      }
                    >
                      <td data-label="Supplier">
                        <div className="supplier-name-cell">
                          <strong>{supplier.name}</strong>

                          <span>
                            {[
                              supplier.city,
                              supplier.county,
                              supplier.country,
                            ]
                              .filter(Boolean)
                              .join(", ") || "No address added"}
                          </span>
                        </div>
                      </td>

                      <td data-label="Contact">
                        <div className="supplier-contact-cell">
                          <span>
                            {supplier.contact_name ||
                              "No contact person"}
                          </span>

                          <small>
                            {supplier.phone ||
                              supplier.email ||
                              "No contact details"}
                          </small>
                        </div>
                      </td>

                      <td data-label="Account">
                        <div className="supplier-contact-cell">
                          <span>
                            {supplier.account_number ||
                              "No account number"}
                          </span>

                          <small>
                            {supplier.vat_number
                              ? `VAT: ${supplier.vat_number}`
                              : "No VAT number"}
                          </small>
                        </div>
                      </td>

                      <td data-label="Payment Terms">
                        {Number(
                          supplier.payment_terms_days || 0
                        ) === 0
                          ? "Due immediately"
                          : `${supplier.payment_terms_days} days`}
                      </td>

                      <td data-label="Status">
                        <span
                          className={`supplier-status ${
                            supplier.active
                              ? "supplier-status-active"
                              : "supplier-status-inactive"
                          }`}
                        >
                          {supplier.active
                            ? "Active"
                            : "Inactive"}
                        </span>
                      </td>

                     <td
  data-label="Actions"
  className="supplier-actions-cell"
>
  <div className="supplier-mobile-actions">
    <button
      type="button"
      className="supplier-action-btn"
      onClick={() => openEditModal(supplier)}
    >
      Edit
    </button>

    {supplier.active ? (
      <button
        type="button"
        className="supplier-action-btn supplier-action-danger"
        onClick={() => deactivateSupplier(supplier)}
      >
        Deactivate
      </button>
    ) : (
      <button
        type="button"
        className="supplier-action-btn supplier-action-success"
        onClick={() => reactivateSupplier(supplier)}
      >
        Reactivate
      </button>
    )}
  </div>
</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {modalOpen && (
        <div
          className="supplier-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeModal();
            }
          }}
        >
          <div
            className="supplier-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="supplier-modal-title"
          >
            <div className="supplier-modal-header">
              <div>
                <p className="suppliers-eyebrow">
                  Supplier details
                </p>

                <h2 id="supplier-modal-title">
                  {editingSupplierId
                    ? "Edit Supplier"
                    : "New Supplier"}
                </h2>
              </div>

              <button
                type="button"
                className="supplier-modal-close"
                onClick={closeModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>

           <form className="supplier-form" onSubmit={saveSupplier}>
  <div className="supplier-form-body">
    {/* =========================
        BASIC INFORMATION
    ========================= */}

    <section className="supplier-form-section">
      <div className="supplier-section-heading">
        <h3>Basic information</h3>
        <p>Main supplier and contact information.</p>
      </div>

      <div className="supplier-form-grid">
        <div className="supplier-field supplier-field-full">
          <label htmlFor="supplier-name">
            Supplier Name <span>*</span>
          </label>

          <input
            id="supplier-name"
            type="text"
            value={form.name}
            onChange={(event) =>
              updateField("name", event.target.value)
            }
            className={fieldErrors.name ? "input-error" : ""}
            autoFocus
          />

          {fieldErrors.name && (
            <small className="supplier-field-error">
              {fieldErrors.name}
            </small>
          )}
        </div>

        <div className="supplier-field">
          <label htmlFor="supplier-contact">
            Contact Person
          </label>

          <input
            id="supplier-contact"
            type="text"
            value={form.contact_name}
            onChange={(event) =>
              updateField("contact_name", event.target.value)
            }
          />
        </div>

        <div className="supplier-field">
          <label htmlFor="supplier-phone">Phone</label>

          <input
            id="supplier-phone"
            type="tel"
            value={form.phone}
            onChange={(event) =>
              updateField("phone", event.target.value)
            }
          />
        </div>

        <div className="supplier-field supplier-field-full">
          <label htmlFor="supplier-email">Email</label>

          <input
            id="supplier-email"
            type="email"
            value={form.email}
            onChange={(event) =>
              updateField("email", event.target.value)
            }
            className={fieldErrors.email ? "input-error" : ""}
          />

          {fieldErrors.email && (
            <small className="supplier-field-error">
              {fieldErrors.email}
            </small>
          )}
        </div>
      </div>
    </section>

    {/* =========================
        ADDRESS
    ========================= */}

    <section className="supplier-form-section">
      <div className="supplier-section-heading">
        <h3>Address</h3>
        <p>Supplier billing or delivery address.</p>
      </div>

      <div className="supplier-form-grid">
        <div className="supplier-field supplier-field-full">
          <label htmlFor="supplier-address-1">
            Address Line 1
          </label>

          <input
            id="supplier-address-1"
            type="text"
            value={form.address_line_1}
            onChange={(event) =>
              updateField("address_line_1", event.target.value)
            }
          />
        </div>

        <div className="supplier-field supplier-field-full">
          <label htmlFor="supplier-address-2">
            Address Line 2
          </label>

          <input
            id="supplier-address-2"
            type="text"
            value={form.address_line_2}
            onChange={(event) =>
              updateField("address_line_2", event.target.value)
            }
          />
        </div>

        <div className="supplier-field">
          <label htmlFor="supplier-city">City</label>

          <input
            id="supplier-city"
            type="text"
            value={form.city}
            onChange={(event) =>
              updateField("city", event.target.value)
            }
          />
        </div>

        <div className="supplier-field">
          <label htmlFor="supplier-county">County</label>

          <input
            id="supplier-county"
            type="text"
            value={form.county}
            onChange={(event) =>
              updateField("county", event.target.value)
            }
          />
        </div>

        <div className="supplier-field">
          <label htmlFor="supplier-postcode">Postcode</label>

          <input
            id="supplier-postcode"
            type="text"
            value={form.postcode}
            onChange={(event) =>
              updateField("postcode", event.target.value)
            }
          />
        </div>

        <div className="supplier-field">
          <label htmlFor="supplier-country">Country</label>

          <input
            id="supplier-country"
            type="text"
            value={form.country}
            onChange={(event) =>
              updateField("country", event.target.value)
            }
          />
        </div>
      </div>
    </section>

    {/* =========================
        ACCOUNT INFORMATION
    ========================= */}

    <section className="supplier-form-section">
      <div className="supplier-section-heading">
        <h3>Account information</h3>
        <p>Details used for ordering and supplier invoices.</p>
      </div>

      <div className="supplier-form-grid">
        <div className="supplier-field">
          <label htmlFor="supplier-account-number">
            Account Number
          </label>

          <input
            id="supplier-account-number"
            type="text"
            value={form.account_number}
            onChange={(event) =>
              updateField("account_number", event.target.value)
            }
          />
        </div>

        <div className="supplier-field">
          <label htmlFor="supplier-vat-number">
            VAT Number
          </label>

          <input
            id="supplier-vat-number"
            type="text"
            value={form.vat_number}
            onChange={(event) =>
              updateField("vat_number", event.target.value)
            }
          />
        </div>

        <div className="supplier-field">
          <label htmlFor="supplier-payment-terms">
            Payment Terms (days)
          </label>

          <input
            id="supplier-payment-terms"
            type="number"
            min="0"
            max="3650"
            step="1"
            value={form.payment_terms_days}
            onChange={(event) =>
              updateField(
                "payment_terms_days",
                event.target.value
              )
            }
            className={
              fieldErrors.payment_terms_days
                ? "input-error"
                : ""
            }
          />

          {fieldErrors.payment_terms_days && (
            <small className="supplier-field-error">
              {fieldErrors.payment_terms_days}
            </small>
          )}
        </div>

        {editingSupplierId && (
          <div className="supplier-field">
            <label htmlFor="supplier-active">Status</label>

            <select
              id="supplier-active"
              value={form.active ? "active" : "inactive"}
              onChange={(event) =>
                updateField(
                  "active",
                  event.target.value === "active"
                )
              }
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        )}

        <div className="supplier-field supplier-field-full">
          <label htmlFor="supplier-notes">Notes</label>

          <textarea
            id="supplier-notes"
            rows="3"
            value={form.notes}
            onChange={(event) =>
              updateField("notes", event.target.value)
            }
            placeholder="Internal notes about this supplier..."
          />
        </div>
      </div>
    </section>
  </div>

  {/* =========================
      FIXED MODAL FOOTER
  ========================= */}

  <div className="supplier-modal-footer">
    <button
      type="button"
      className="supplier-secondary-btn"
      onClick={closeModal}
      disabled={saving}
    >
      Cancel
    </button>

    <button
      type="submit"
      className="supplier-primary-btn"
      disabled={saving}
    >
      {saving
        ? "Saving..."
        : editingSupplierId
          ? "Update Supplier"
          : "Save Supplier"}
    </button>
  </div>
</form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuppliersPage;